/**
 * Files + document signatures (Loops D6/D7) — typed wrappers. Staff calls
 * are authenticated; the `sign/{token}` calls are public (the emailed,
 * expiring link token is the credential).
 *
 *   GET  /files/{id}                                      getFile
 *   GET  /files/{id}/signature-request                    getDocumentSignatureStatus → envelope | null
 *   POST /files/signature-status                          getDocumentSignatureStatuses → { [id]: envelope } (D11)
 *   POST /files/{id}/signature-request                    requestDocumentSignature
 *   POST /files/{id}/signature-request/resend             resendDocumentSignature
 *   POST /files/{id}/signature-request/void               voidDocumentSignature
 *   POST /files/{id}/signature-request/signed-copy/retry  retrySignedCopy
 *   GET  /sign/{token}/meta                               getDocumentSignMeta
 *   POST /sign/{token}                                    submitDocumentSignature
 *   GET  /files/{id}/signature-request/page-geometry      getDocumentPageGeometry (D13)
 *   GET  /files/{id}/content · /sign/{token}/document     fetchFilePdfBytes · fetchDocumentSignPdf (D13)
 */
import { apiClient } from './client';

export async function getFile(fileId) {
  const { data } = await apiClient.get(`/files/${fileId}`);
  return data;
}

export async function getDocumentSignatureStatus(fileId) {
  const { data } = await apiClient.get(`/files/${fileId}/signature-request`);
  return data?.envelope || null;
}

/** Loop D11 — { [fileId]: envelope } for the files that have a request (≤500 ids; absent = no request). */
export async function getDocumentSignatureStatuses(fileIds) {
  const { data } = await apiClient.post('/files/signature-status', { file_ids: fileIds });
  return data || {};
}

/** payload: { recipient: {name, email}, message, expires_in_days } */
export async function requestDocumentSignature(fileId, payload) {
  const { data } = await apiClient.post(`/files/${fileId}/signature-request`, payload);
  return data;
}

/** Rotates the link — the previously emailed link stops working. */
export async function resendDocumentSignature(fileId, signer) {
  const { data } = await apiClient.post(`/files/${fileId}/signature-request/resend`, signer ? { signer } : {});
  return data;
}

export async function voidDocumentSignature(fileId, reason = '') {
  const { data } = await apiClient.post(`/files/${fileId}/signature-request/void`, { reason });
  return data;
}

/** One deliberate signed-copy attempt (past the server's auto-retry back-off and cap). */
export async function retrySignedCopy(fileId) {
  const { data } = await apiClient.post(`/files/${fileId}/signature-request/signed-copy/retry`, {});
  return data;
}

export async function getDocumentSignMeta(token) {
  const { data } = await apiClient.get(`/sign/${encodeURIComponent(token)}/meta`);
  return data;
}

/** payload: { name, data_url, signature_type, email? } — plus `initials_data_url` only when the request has initials fields (D12). */
export async function submitDocumentSignature(token, payload) {
  const { data } = await apiClient.post(`/sign/${encodeURIComponent(token)}`, payload);
  return data;
}

/** Absolute URL for meta's token-gated `document_url` (iframe src + new-tab link). */
export function documentSignAssetUrl(documentUrl) {
  return documentUrl ? `${process.env.REACT_APP_BACKEND_URL}${documentUrl}` : '';
}

/** Loop D13 — per-page { page, media_box, crop_box, rotate } of an eligible PDF (placement step). */
export async function getDocumentPageGeometry(fileId) {
  const { data } = await apiClient.get(`/files/${fileId}/signature-request/page-geometry`);
  return data?.pages || [];
}

/** PDF bytes as an ArrayBuffer; a JSON error body is decoded so its message survives. */
async function getPdfBytes(url) {
  try {
    const { data } = await apiClient.get(url, { responseType: 'arraybuffer' });
    return data;
  } catch (e) {
    const body = e?.response?.data;
    if (body instanceof ArrayBuffer) {
      try { e.response.data = JSON.parse(new TextDecoder().decode(body)); } catch { e.response.data = {}; }
    }
    throw e;
  }
}

/** Loop D13 — the original's bytes for the sender's placement step. */
export const fetchFilePdfBytes = (fileId) => getPdfBytes(`/files/${fileId}/content`);

/** Loop D13 — the signer's token-gated PDF bytes (fields envelopes only). */
export const fetchDocumentSignPdf = (documentUrl) => getPdfBytes(documentSignAssetUrl(documentUrl));
